import type { IconType } from '@assembly-js/design-system';

import {
  getVisibleSettingsSections,
  isSettingsPageActive,
  SETTINGS_PAGES,
  SettingsPage,
  SettingsVisibilityContext,
} from 'src/features/settings/registry/settingsRegistry';

/**
 * `classicUpgrade` is the diamond Classic workspaces have always shown;
 * `planTag` names the plan a new-pricing workspace needs.
 */
export type SettingsSidebarPlanBadge =
  | { kind: 'classicUpgrade' }
  | { kind: 'planTag'; label: string };

export interface SettingsSidebarRow {
  id: string;
  label: string;
  path: string;
  iconName: IconType;
  selected: boolean;
  planBadge: SettingsSidebarPlanBadge | null;
}

export interface SettingsSidebarSection {
  id: string;
  title: string;
  rows: SettingsSidebarRow[];
}

export const getSettingsSidebarPlanBadge = (
  page: SettingsPage,
  ctx: SettingsVisibilityContext,
): SettingsSidebarPlanBadge | null => {
  if (!page.planBadge) return null;
  const { studio } = ctx.planStatus;
  if (!studio.isStudioPricingWorkspace) {
    return page.planBadge.showOnClassic?.(ctx)
      ? { kind: 'classicUpgrade' }
      : null;
  }
  // Entitlements read as freemium until the plan loads; a tag now would flash.
  if (studio.isCurrentPlanLoading) return null;
  const label = studio.upgradeBadgeLabel(page.planBadge.feature);
  return label ? { kind: 'planTag', label } : null;
};

/** The settings sidebar under `new-settings`, built entirely from the registry. */
export const getSettingsSidebarSections = ({
  ctx,
  clientTerm,
  pathname,
  pages = SETTINGS_PAGES,
}: {
  ctx: SettingsVisibilityContext;
  clientTerm: string;
  pathname: string;
  pages?: ReadonlyArray<SettingsPage>;
}): SettingsSidebarSection[] =>
  getVisibleSettingsSections(ctx, clientTerm, pages).map((section) => ({
    id: section.id,
    title: section.title,
    rows: section.pages.map((page) => ({
      id: page.id,
      label: page.label,
      path: page.path,
      iconName: page.iconName,
      selected: isSettingsPageActive(page, pathname, clientTerm),
      planBadge: getSettingsSidebarPlanBadge(page, ctx),
    })),
  }));
